import clsx from "clsx";
import { ChevronDown, Search } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import type { CaseCounts, CaseListItem } from "../../../../shared/contracts.js";
import {
  flattenCasePages,
  useCaseList,
  type CaseListPages,
} from "../../../api/caseQueries.js";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import { AsyncPanel } from "../../../ui/AsyncPanel.js";
import { Button } from "../../../ui/Button.js";
import {
  COUNT_PRESENTATION,
  formatCount,
  PARTS_VIEW_PRESENTATION,
  STATUS_PRESENTATION,
} from "../presentation.js";
import { CaseListRow } from "./CaseListRow.js";
import type { WorkspaceUrlStateApi } from "./useWorkspaceUrlState.js";

/**
 * The centre list of the Parts workspace (spec §10 "Center list"): the
 * view's name, the three counts (needs attention, partial arrivals, arrived
 * inside 48 hours), one search field, the No ETA and Pre-RO filters, and the
 * rows themselves with "Show more" for the next page.
 *
 * Search and filters are URL state ({@link useWorkspaceUrlState}); the field
 * keeps a local draft and writes it to the address after a short pause so a
 * reload restores the search without a history entry per keystroke. Every
 * non-happy state — first load, empty view, empty search, offline, denied,
 * retry — is `AsyncPanel`'s. A refresh that fails once rows are on screen is
 * its notice above them; the rows stay.
 */

export interface CaseListPaneProps {
  url: WorkspaceUrlStateApi;
  counts?: CaseCounts;
  className?: string;
}

/** How long typing pauses before the search reaches the URL and the server. */
export const SEARCH_DEBOUNCE_MS = 250;

export function CaseListPane({ url, counts, className }: CaseListPaneProps) {
  const { state } = url;
  const headingId = useId();
  const searchId = useId();
  const presentation = PARTS_VIEW_PRESENTATION[state.view];

  const list = useCaseList({
    view: state.view,
    query: state.query,
    noEta: state.noEta,
    preRo: state.preRo,
  });

  return (
    <section
      className={clsx("workspace__list", "list-pane", className)}
      aria-labelledby={headingId}
    >
      <header className="list-pane__header">
        <div className="list-pane__title">
          <h1 id={headingId} className="list-pane__heading">
            {presentation.label}
          </h1>
          <p className="list-pane__description">{presentation.description}</p>
        </div>

        <CountsStrip counts={counts} />

        <div className="list-pane__controls">
          <SearchField
            id={searchId}
            value={state.query}
            onCommit={url.setQuery}
          />
          <div
            className="list-pane__filters"
            role="group"
            aria-label="Filters"
          >
            <FilterToggle
              label="No ETA"
              checked={state.noEta}
              onChange={url.setNoEta}
            />
            <FilterToggle
              label="Pre-RO"
              checked={state.preRo}
              onChange={url.setPreRo}
            />
          </div>
        </div>
      </header>

      <CaseRows
        list={list}
        selectedCaseId={state.caseId}
        onSelect={url.selectCase}
        hasFilters={Boolean(state.query) || state.noEta || state.preRo}
        emptyTitle={presentation.emptyTitle}
        emptyDescription={presentation.emptyDescription}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Counts
// ---------------------------------------------------------------------------

function CountsStrip({ counts }: { counts?: CaseCounts }) {
  return (
    <section className="counts" aria-label="Counts">
      <dl className="counts__list">
        {COUNT_PRESENTATION.map((count) => {
          const Icon = STATUS_PRESENTATION[count.tone].Icon;
          const value = counts ? counts[count.key] : null;
          return (
            <div key={count.key} className="counts__item" data-tone={count.tone}>
              <dt className="counts__label">
                <Icon
                  className="counts__icon"
                  aria-hidden="true"
                  focusable="false"
                />
                {count.label}
              </dt>
              <dd className="counts__value">
                {value === null ? (
                  <span className="counts__pending">
                    <span aria-hidden="true">—</span>
                    <span className="visually-hidden">Loading</span>
                  </span>
                ) : (
                  formatCount(value)
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Search and filters
// ---------------------------------------------------------------------------

function SearchField({
  id,
  value,
  onCommit,
}: {
  id: string;
  value: string;
  onCommit: (query: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const committed = useRef(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The URL changed from outside the field (reload, Back, a shared link):
  // adopt it. A change the field itself just committed is already the draft.
  useEffect(() => {
    if (value !== committed.current) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      if (trimmed === committed.current) return;
      committed.current = trimmed;
      onCommit(trimmed);
    },
    [onCommit],
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setDraft(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(next), SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className="list-pane__search" role="search">
      <Search
        className="list-pane__search-icon"
        aria-hidden="true"
        focusable="false"
      />
      <input
        id={id}
        type="search"
        className="text-input list-pane__search-input"
        data-size="desktop"
        aria-label="Search this list"
        placeholder="RO, case, VIN, customer, part, advisor…"
        autoComplete="off"
        spellCheck={false}
        value={draft}
        onChange={onChange}
        onBlur={() => {
          if (timer.current) clearTimeout(timer.current);
          commit(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            if (timer.current) clearTimeout(timer.current);
            commit(draft);
          }
        }}
      />
    </div>
  );
}

/**
 * A URL-backed checkbox. The box flips at once (local state) and the address
 * follows: the router applies a navigation as a transition, and a controlled
 * checkbox that waits for it would be snapped back to unchecked by React
 * until the new address arrived.
 */
function FilterToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  const [draft, setDraft] = useState(checked);
  useEffect(() => {
    setDraft(checked);
  }, [checked]);

  return (
    <label className={clsx("filter-toggle", draft && "filter-toggle--on")}>
      <input
        type="checkbox"
        className="filter-toggle__input"
        checked={draft}
        onChange={(event) => {
          setDraft(event.target.checked);
          onChange(event.target.checked);
        }}
      />
      <span className="filter-toggle__label">{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function CaseRows({
  list,
  selectedCaseId,
  onSelect,
  hasFilters,
  emptyTitle,
  emptyDescription,
}: {
  list: UseInfiniteQueryResult<CaseListPages>;
  selectedCaseId: string | null;
  onSelect: (caseId: string) => void;
  hasFilters: boolean;
  emptyTitle: string;
  emptyDescription: string;
}) {
  const rows: readonly CaseListItem[] = flattenCasePages(list.data);
  const loaded = list.data !== undefined;
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const moveFocus = (index: number, direction: -1 | 1) => {
    const next = rowRefs.current[index + direction];
    next?.focus();
  };

  return (
    <div
      className="list-pane__body"
      data-updating={list.isPlaceholderData ? "true" : undefined}
    >
      <AsyncPanel
        isLoading={list.isPending}
        error={loaded ? undefined : list.error}
        refreshError={loaded ? list.error : undefined}
        refreshTitle={
          list.isFetchNextPageError
            ? "Couldn't load more orders."
            : "Couldn't refresh the list."
        }
        isEmpty={rows.length === 0}
        onRetry={() => {
          void list.refetch();
        }}
        loadingLabel="Loading orders…"
        emptyTitle={hasFilters ? "No orders match" : emptyTitle}
        emptyDescription={
          hasFilters
            ? "Try a different search, or clear the No ETA and Pre-RO filters."
            : emptyDescription
        }
      >
        <ul className="case-list" aria-label="Cases">
          {rows.map((item, index) => (
            <CaseListRow
              key={item.id}
              ref={(element) => {
                rowRefs.current[index] = element;
              }}
              item={item}
              selected={item.id === selectedCaseId}
              onSelect={onSelect}
              onMoveFocus={(direction) => moveFocus(index, direction)}
            />
          ))}
        </ul>
        {list.hasNextPage ? (
          <div className="list-pane__more">
            <Button
              variant="secondary"
              icon={<ChevronDown />}
              busy={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              Show more
            </Button>
          </div>
        ) : null}
      </AsyncPanel>
    </div>
  );
}
